import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { SEO } from "@/components/SEO";

const PREFS = [
  { key: "bookings", label: "Booking emails", desc: "Confirmations, reminders, day-after follow-ups, and review invitations for your sessions." },
  { key: "community", label: "Community & rewards", desc: "Badge unlocks, Ripple milestones, and membership updates." },
  { key: "newsletter", label: "Quarterly newsletter", desc: "A seasonal letter from The Team — new practitioners, Learn Library gems, and community news." },
];

export default function EmailPreferences() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/emails/preferences").then((r) => setPrefs(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);

  const toggle = async (key, value) => {
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    setSaving(true);
    try {
      await api.put("/emails/preferences", next);
      toast.success("Preferences saved");
    } catch (e) {
      toast.error(apiError(e));
      setPrefs(prefs);
    } finally { setSaving(false); }
  };

  if (!prefs) return <div className="py-32 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="silk-hero min-h-[70vh] px-5 sm:px-8 py-16">
      <SEO title="Email preferences — O.R.E.C" description="Choose which emails you receive from O.R.E.C." />
      <div className="max-w-2xl mx-auto" data-testid="email-preferences-page">
        <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-3">Your inbox, your call</p>
        <h1 className="font-display text-4xl sm:text-5xl text-foreground mb-3">Email preferences</h1>
        <p className="text-sm text-muted-foreground mb-10 max-w-xl leading-relaxed">
          Choose what lands in your inbox. Changes take effect immediately.
        </p>

        <div className="space-y-4">
          {PREFS.map((p) => (
            <div key={p.key} className="silk-card rounded-lg p-6 flex items-start justify-between gap-6" data-testid={`pref-row-${p.key}`}>
              <div>
                <p className="text-base text-foreground mb-1">{p.label}</p>
                <p className="text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
              </div>
              <Switch checked={prefs[p.key]} disabled={saving} onCheckedChange={(v) => toggle(p.key, v)}
                data-testid={`pref-toggle-${p.key}`} className="mt-1 shrink-0" />
            </div>
          ))}
        </div>

        <p className="text-xs text-muted-foreground mt-8 leading-relaxed" data-testid="email-prefs-security-note">
          Security and account emails — like password resets and email-change confirmations — are always sent, so your account stays safe.
        </p>
      </div>
    </div>
  );
}
